import { atom, selector } from 'recoil';

// NOTE: atom_navigatorModeEnabled was removed — tracker now reads atom_isNavigationMode directly.

export interface GuideStep {
	instruction: string;           // plain text from ORS
	distance: number;              // meters — distance this step covers
	duration: number;              // seconds
	type: number;                  // ORS maneuver type (0=left, 1=right, 5=slight right, 10=arrive, 11=depart, 12/13=keep left/right, etc.)
	name: string;                  // street name (may be "-")
	endCoord: { lat: number; lng: number }; // resolved from way_points[1] at parse time (no index math at runtime)
}

export interface GuideRouteSegment {
	fromStopId: string;
	toStopId: string;
	polylineCoords: { lat: number; lng: number }[];
	steps: GuideStep[];
}

export interface GuideRoute {
	stopOrder: string[];
	segments: GuideRouteSegment[];
}

export const atom_guideRoute = atom<GuideRoute | null>({
	key: 'guideRoute',
	default: null,
});

export interface RouteTrackerState {
	isActive: boolean;
	routeType: 'single-stop' | 'multi-stop' | 'guide-route' | null;
	targetStopId: string | null;
	distanceToTarget: number | null;
	currentStopIndex: number;
	closestStopIndex: number | null;
	skippedStopIds: string[];
	status: 'tracking' | 'arrived' | 'off-route' | 'skipped-stop' | 'completed';
	lastArrivalNotified: string | null;
	lastOffRouteNotified: number | null;
	lastSkippedNotified: string | null;
	/** Index of the current ORS step within the current guide-route segment. */
	currentGuideStepIndex: number;
}

export const atom_routeTrackerState = atom<RouteTrackerState>({
	key: 'routeTrackerState',
	default: {
		isActive: false,
		routeType: null,
		targetStopId: null,
		distanceToTarget: null,
		currentStopIndex: 0,
		closestStopIndex: null,
		skippedStopIds: [],
		status: 'tracking',
		lastArrivalNotified: null,
		lastOffRouteNotified: null,
		lastSkippedNotified: null,
		currentGuideStepIndex: 0,
	},
});

// Per-field selectors — consumers that only need one of these won't re-render
// when other fields (e.g. distanceToTarget) churn every GPS tick.
export const selector_isTrackerActive = selector<boolean>({
	key: 'routeTracker_isActive',
	get: ({ get }) => get(atom_routeTrackerState).isActive,
});

export const selector_trackerRouteType = selector<RouteTrackerState['routeType']>({
	key: 'routeTracker_routeType',
	get: ({ get }) => get(atom_routeTrackerState).routeType,
});

export const selector_trackerTargetStopId = selector<string | null>({
	key: 'routeTracker_targetStopId',
	get: ({ get }) => get(atom_routeTrackerState).targetStopId,
});

export const selector_trackerCurrentGuideStepIndex = selector<number>({
	key: 'routeTracker_currentGuideStepIndex',
	get: ({ get }) => get(atom_routeTrackerState).currentGuideStepIndex,
});

export const selector_trackerDistanceToTarget = selector<number | null>({
	key: 'routeTracker_distanceToTarget',
	get: ({ get }) => get(atom_routeTrackerState).distanceToTarget,
});
